import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  BorrowingDueItem, MaturityLadderReport, OdPositionReport, TieOutReport, UtilisationReport,
} from '../../interfaces/borrowing.interface';
import { BorrowingService } from '../../services/borrowing.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { helpElement } from '../../help/attrs';

type HeadCol = string | [string, boolean] | { label: string; right?: boolean; helpKey: string };
const Head: React.FC<{ cols: HeadCol[] }> = ({ cols }) => (
  <TableHead><TableRow>{cols.map(c => {
    if (typeof c === 'object' && !Array.isArray(c)) {
      return <TableCell key={c.label} align={c.right ? 'right' : 'left'} {...helpElement(c.helpKey)}>{c.label}</TableCell>;
    }
    const [label, right] = typeof c === 'string' ? [c, false] : c;
    return <TableCell key={label} align={right ? 'right' : 'left'}>{label}</TableCell>;
  })}</TableRow></TableHead>
);

const BorrowingReports: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const [tab, setTab] = useState(0);
  const [asOf, setAsOf] = useState(toLocalIsoDate());
  const [util, setUtil] = useState<UtilisationReport | null>(null);
  const [od, setOd] = useState<OdPositionReport | null>(null);
  const [ladder, setLadder] = useState<MaturityLadderReport | null>(null);
  const [tie, setTie] = useState<TieOutReport | null>(null);
  const [due, setDue] = useState<BorrowingDueItem[]>([]);

  const load = useCallback(async () => {
    try {
      const horizon = new Date(); horizon.setDate(horizon.getDate() + 90);
      const [u, o, l, t, d] = await Promise.all([
        BorrowingService.utilisation(institutionId, asOf), BorrowingService.odPosition(institutionId, asOf),
        BorrowingService.maturity(institutionId, asOf), BorrowingService.tieOut(institutionId, asOf),
        BorrowingService.dueItems(institutionId, toLocalIsoDate(), toLocalIsoDate(horizon)),
      ]);
      setUtil(u); setOd(o); setLadder(l); setTie(t); setDue(d);
    } catch (err) {
      toast.showApiError(err, 'Could not load the borrowing reports');
    }
  }, [institutionId, asOf, toast]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box data-help-page="financing.reports.page" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flex: '1 1 240px' }}>Borrowing reports</Typography>
        <TextField data-help="financing.reports.asOf" sx={{ flex: '1 1 160px', maxWidth: 200 }} size="small" type="date" label="As of" InputLabelProps={{ shrink: true }} value={asOf} onChange={e => setAsOf(e.target.value)} />
        <Button component={RouterLink} to="/financing">Facilities</Button>
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ mb: 2 }}>
        <Tab data-help="financing.reports.tab.utilisation" label="Utilisation" /><Tab data-help="financing.reports.tab.od" label="Overdraft position" /><Tab data-help="financing.reports.tab.ladder" label="Maturity ladder" /><Tab data-help="financing.reports.tab.tie" label="GL tie-out" /><Tab data-help="financing.reports.tab.due" label="Due soon" />
      </Tabs>

      {tab === 0 && util && (
        <TableContainer component={Paper}><Table data-help-section="financing.reports.utilisation" size="small">
          <Head cols={[{ label: 'Facility', helpKey: 'financing.reports.utilisation.col.facility' }, { label: 'Type', helpKey: 'financing.reports.utilisation.col.type' }, { label: 'Limit', right: true, helpKey: 'financing.reports.utilisation.col.limit' }, { label: 'Outstanding', right: true, helpKey: 'financing.reports.utilisation.col.outstanding' }, { label: 'Available', right: true, helpKey: 'financing.reports.utilisation.col.available' }, { label: 'Used %', right: true, helpKey: 'financing.reports.utilisation.col.usedPct' }, { label: 'Valid to', helpKey: 'financing.reports.utilisation.col.validTo' }, { label: 'Accrued interest', right: true, helpKey: 'financing.reports.utilisation.col.accrued' }]} />
          <TableBody>
            {util.rows.map(r => (
              <TableRow key={r.facilityId}>
                <TableCell>{r.facilityNo} - {r.name}</TableCell><TableCell>{humanizeEnum(r.facilityType)}</TableCell>
                <TableCell align="right">{formatMoney(r.sanctionedLimit, r.currencyCode)}</TableCell><TableCell align="right">{formatMoney(r.outstanding, r.currencyCode)}</TableCell>
                <TableCell align="right">{formatMoney(r.available, r.currencyCode)}</TableCell><TableCell align="right">{r.utilisationPct ?? '-'}</TableCell>
                <TableCell>{r.validTo ? formatDate(r.validTo) : '-'} {r.expiringSoon && <Chip size="small" color="warning" label="Expiring" />}</TableCell>
                <TableCell align="right">{formatMoney(r.accruedInterestUnpaid)}</TableCell>
              </TableRow>
            ))}
            <TableRow><TableCell colSpan={2}><b>Total (BDT facilities)</b></TableCell><TableCell align="right"><b>{formatMoney(util.totalLimit)}</b></TableCell>
              <TableCell align="right"><b>{formatMoney(util.totalOutstanding)}</b></TableCell><TableCell colSpan={4} /></TableRow>
            {util.rows.some(r => r.currencyCode && r.currencyCode !== 'BDT') && (
              <TableRow><TableCell colSpan={8}>
                <Typography variant="caption" color="text.secondary">
                  Foreign-currency facilities are shown in their own currency and are not included in the BDT totals.
                </Typography>
              </TableCell></TableRow>
            )}
          </TableBody></Table></TableContainer>
      )}

      {tab === 1 && od && (
        od.rows.length === 0 ? <Alert severity="info">No overdraft facilities.</Alert> : (
          <TableContainer component={Paper}><Table data-help-section="financing.reports.od" size="small">
            <Head cols={[{ label: 'Bank account', helpKey: 'financing.reports.od.col.bank' }, { label: 'Facility', helpKey: 'financing.reports.od.col.facility' }, { label: 'Limit', right: true, helpKey: 'financing.reports.od.col.limit' }, { label: 'Utilised', right: true, helpKey: 'financing.reports.od.col.utilised' }, { label: 'Headroom', right: true, helpKey: 'financing.reports.od.col.headroom' }, { label: 'Status', helpKey: 'financing.reports.od.col.status' }]} />
            <TableBody>
              {od.rows.map((r, i) => (
                <TableRow key={r.facilityId ?? i}>
                  <TableCell>{r.bankName ?? '-'}</TableCell><TableCell>{r.facilityNo ?? '-'}</TableCell>
                  <TableCell align="right">{formatMoney(r.limit)}</TableCell><TableCell align="right">{formatMoney(r.utilised)}</TableCell>
                  <TableCell align="right">{formatMoney(r.headroom)}</TableCell>
                  <TableCell>{r.overLimit ? <Chip size="small" color="error" label="Over limit" /> : <Chip size="small" color="success" label="Within limit" />}</TableCell>
                </TableRow>
              ))}
            </TableBody></Table></TableContainer>
        )
      )}

      {tab === 2 && ladder && (
        <>
          <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 3 }} data-testid="current-split">
            <Typography>Current (due within 12 months): <b>{formatMoney(ladder.current)}</b></Typography>
            <Typography>Non-current: <b>{formatMoney(ladder.nonCurrent)}</b></Typography>
            <Typography>Total outstanding: <b>{formatMoney(ladder.totalOutstanding)}</b></Typography>
          </Paper>
          <TableContainer component={Paper}><Table data-help-section="financing.reports.ladder" size="small">
            <Head cols={['Facility', ...ladder.buckets.map(b => [b.label, true] as [string, boolean]), ['Current', true], ['Non-current', true]]} />
            <TableBody>
              {ladder.facilities.map(f => (
                <TableRow key={f.facilityId}>
                  <TableCell>{f.facilityNo} - {f.name}</TableCell>
                  {ladder.buckets.map(b => <TableCell key={b.key} align="right">{formatMoney(f.buckets[b.key] ?? 0)}</TableCell>)}
                  <TableCell align="right">{formatMoney(f.current)}</TableCell><TableCell align="right">{formatMoney(f.nonCurrent)}</TableCell>
                </TableRow>
              ))}
              <TableRow><TableCell><b>Total</b></TableCell>
                {ladder.buckets.map(b => <TableCell key={b.key} align="right"><b>{formatMoney(b.amount)}</b></TableCell>)}
                <TableCell align="right"><b>{formatMoney(ladder.current)}</b></TableCell><TableCell align="right"><b>{formatMoney(ladder.nonCurrent)}</b></TableCell></TableRow>
            </TableBody></Table></TableContainer>
        </>
      )}

      {tab === 3 && tie && (
        <>
          <Alert severity={tie.tiesOut ? 'success' : 'warning'} sx={{ mb: 2 }} data-testid="tie-out-status">
            {tie.tiesOut ? 'The register ties to the GL.' : 'The register differs from the GL. Opening balances and manual journals on these accounts show up as a difference.'}
          </Alert>
          <TableContainer component={Paper}><Table data-help-section="financing.reports.tie" size="small">
            <Head cols={[{ label: 'Kind', helpKey: 'financing.reports.tie.col.kind' }, { label: 'Account', helpKey: 'financing.reports.tie.col.account' }, { label: 'Register', right: true, helpKey: 'financing.reports.tie.col.register' }, { label: 'GL', right: true, helpKey: 'financing.reports.tie.col.gl' }, { label: 'Difference', right: true, helpKey: 'financing.reports.tie.col.difference' }]} />
            <TableBody>
              {tie.rows.map((r, i) => (
                <TableRow key={i}><TableCell>{humanizeEnum(r.kind)}</TableCell><TableCell>{r.accountCode ? `${r.accountCode} - ${r.accountName}` : '-'}</TableCell>
                  <TableCell align="right">{formatMoney(r.registerAmount)}</TableCell><TableCell align="right">{formatMoney(r.glAmount)}</TableCell>
                  <TableCell align="right">{formatMoney(r.difference)}</TableCell></TableRow>
              ))}
            </TableBody></Table></TableContainer>
        </>
      )}

      {tab === 4 && (
        due.length === 0 ? <Alert severity="info">Nothing due in the next 90 days.</Alert> : (
          <TableContainer component={Paper}><Table data-help-section="financing.reports.due" size="small">
            <Head cols={[{ label: 'Due', helpKey: 'financing.reports.due.col.due' }, { label: 'Item', helpKey: 'financing.reports.due.col.item' }, { label: 'Amount', right: true, helpKey: 'financing.reports.due.col.amount' }, { label: 'Severity', helpKey: 'financing.reports.due.col.severity' }]} />
            <TableBody>
              {due.map(d => (
                <TableRow key={`${d.sourceKey}-${d.documentId}-${d.dueDate}`}>
                  <TableCell>{formatDate(d.dueDate)}</TableCell><TableCell>{d.title}</TableCell>
                  <TableCell align="right">{formatMoney(d.amount, d.currencyCode)}</TableCell><TableCell>{humanizeEnum(d.severity)}</TableCell>
                </TableRow>
              ))}
            </TableBody></Table></TableContainer>
        )
      )}
    </Box>
  );
};

export default BorrowingReports;
